import {
  Alert,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { FC, useCallback, useEffect, useState } from 'react'
import { estruturacaoConsultorService } from '@/services/estruturacaoConsultorService'
import { DefesaRastreabilidade, STATUS_DEFESA_LABEL } from '@/types/estruturacao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { formatDateTime } from '@/utils/formatters'

interface Props {
  reservaId: string
}

/**
 * Etapa 4 — Defesa de crédito, SOMENTE LEITURA: "Em preparação pela Originei" + linha do
 * tempo derivada (`defesa/rastreabilidade`). A minuta NUNCA aparece aqui — nem para o
 * back-office nesta tela (a edição vive na página própria do back-office).
 */
export const EtapaDefesa: FC<Props> = ({ reservaId }) => {
  const [rastreabilidade, setRastreabilidade] = useState<DefesaRastreabilidade | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(false)
    try {
      setRastreabilidade(await estruturacaoConsultorService.rastreabilidade(reservaId))
    } catch {
      setErro(true)
    } finally {
      setCarregando(false)
    }
  }, [reservaId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (carregando) return <EstadoDeLista estado="carregando" />
  if (erro || !rastreabilidade) {
    return (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar o andamento da defesa"
        acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
      />
    )
  }

  const statusLabel = rastreabilidade.statusDefesa
    ? STATUS_DEFESA_LABEL[rastreabilidade.statusDefesa]
    : 'Não iniciada'

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        A defesa de crédito está em preparação pela Originei. Você acompanha o andamento
        por aqui; o conteúdo é conduzido pelo time Originei.
      </Alert>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Situação da defesa:
        </Typography>
        <Chip
          size="small"
          label={statusLabel}
          color={rastreabilidade.statusDefesa === 'APROVADA' ? 'success' : 'default'}
        />
      </Stack>

      <List disablePadding>
        {rastreabilidade.etapas.map((etapa) => (
          <ListItem key={etapa.codigo} sx={{ py: 0.75 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              {etapa.concluida ? (
                <CheckCircleIcon color="success" />
              ) : (
                <RadioButtonUncheckedIcon color="disabled" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={etapa.rotulo}
              primaryTypographyProps={{
                fontWeight: etapa.concluida ? 600 : 400,
                fontSize: '0.9rem',
              }}
              secondary={
                etapa.data
                  ? formatDateTime(etapa.data)
                  : etapa.concluida
                    ? 'Data não registrada'
                    : 'Pendente'
              }
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  )
}

export default EtapaDefesa
